<script setup lang="ts">
import { useField, useForm } from "vee-validate";
import { toTypedSchema } from "@vee-validate/zod";
import * as zod from "zod";
import { onMounted, ref } from "vue";
import router from "@/router";
import BaseLoading from "@/components/Common/BaseLoading.vue";
import { toast } from "vue-sonner";
import { useShipManagementCompanyStore } from "@/stores/master/ship-management-company";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import { ShipManagementCompanyPayload } from "@/types/interface/ship-management-company";
import { mapOptionMultiselect } from "@/utils/helper";
import { useRegionStore } from "@/stores/master/region";

const idShipManagementCompany = ref<string>("");
const isEdit = ref<boolean>(false);
const isLoading = ref<boolean>(false);
const shipManagementCompanyStore = useShipManagementCompanyStore();
const regionStore = useRegionStore();
const detailShipCompany = ref<any>();
const listCountry = ref([]);

const validationSchema = toTypedSchema(
  zod.object({
    name: zod.string().nonempty({ message: "Name is required" }),
    reg_no: zod
      .string()
      .nonempty({ message: "Registration Number is required" }),
    country: zod.preprocess(
      (val) => (val && typeof val === "object" ? val.value : val),
      zod.number()
    ),
  })
);

const { handleSubmit, errors } = useForm({
  validationSchema,
});

const { value: name } = useField<string>("name");
const { value: reg_no } = useField<string>("reg_no");
const { value: country } = useField<string>("country");

const onSubmit = handleSubmit(async (values) => {
  isLoading.value = true;
  const payloadPosition: ShipManagementCompanyPayload = {
    name: name.value,
    reg_no: reg_no.value,
    country: country.value?.value,
  };

  const responseApi = ref<any>();
  if (isEdit.value == true) {
    responseApi.value = await shipManagementCompanyStore.dispatchUpdate(
      idShipManagementCompany.value,
      payloadPosition
    );
  } else {
    responseApi.value = await shipManagementCompanyStore.dispatchCreate(
      payloadPosition
    );
  }
  const { success, message } = responseApi.value;

  if (success == true) {
    setTimeout(() => {
      isLoading.value = false;
      toast.success(
        isEdit.value
          ? "Ship Management Company Successfully Updated"
          : "Ship Management Company Successfully Added"
      );
      router.push("/master/ship-management-company");
    }, 1000);
  } else {
    isLoading.value = false;
    toast.error(message);
  }
});

const loadDetailShipManagementCompany = async () => {
  const { success, message } = await shipManagementCompanyStore.dispatchDetail(
    idShipManagementCompany.value
  );
  if (success) {
    detailShipCompany.value = await shipManagementCompanyStore.company.data;
    name.value = detailShipCompany.value.name;
    reg_no.value = detailShipCompany.value.reg_no;
    country.value = mapOptionMultiselect(
      detailShipCompany.value.country,
      "id",
      "name"
    );
  }
};

const loadListCountry = async () => {
  const { success } = await regionStore.dispatchGetCountry();
  listCountry.value = mapOptionMultiselect(regionStore.countries, "Id", "Name");
};

onMounted(() => {
  loadListCountry();
  if (router.currentRoute.value.params.id != undefined) {
    isEdit.value = true;
    idShipManagementCompany.value =
      router.currentRoute.value.params.id.toString();
    loadDetailShipManagementCompany();
  }
});
</script>
<template>
  <div class="row justify-content-center">
    <div class="col-lg-12">
      <div class="card bg-white border-0 rounded-3 mb-4">
        <div class="card-body p-4">
          <div
            class="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3 mb-lg-4"
          >
            <h3 class="mb-0">Form Create Ship Management Company</h3>
            <a
              class="btn btn-danger fw-semibold text-white px-3"
              type="button"
              href="/master/ship-management-company"
            >
              <i class="ri-arrow-drop-left-line text-gray-light"></i>
              Back To List Ship Management Company
            </a>
          </div>
          <BaseLoading text="Processing Data..." v-if="isLoading" />
          <form
            @submit="onSubmit"
            class="row g-3 needs-validation form"
            v-if="!isLoading"
          >
            <div class="row">
              <div class="col-lg-6">
                <div class="form-group">
                  <label for="name" class="form-label label text-secondary"
                    >Name <span class="text-danger">*</span></label
                  >
                  <div class="position-relative">
                    <input
                      name="name"
                      v-model="name"
                      placeholder="Name"
                      type="text"
                      class="form-control text-dark"
                    />
                  </div>
                  <span class="text-danger">{{ errors.name }}</span>
                </div>
              </div>
              <div class="col-lg-6">
                <div class="form-group">
                  <label for="reg_no" class="form-label label text-secondary"
                    >Registration Number
                    <span class="text-danger">*</span></label
                  >
                  <div class="position-relative">
                    <input
                      name="reg_no"
                      v-model="reg_no"
                      placeholder="Ship Management Company Name"
                      type="text"
                      class="form-control text-dark"
                    />
                  </div>
                  <span class="text-danger">{{ errors.reg_no }}</span>
                </div>
              </div>
              <div class="col-lg-6">
                <div class="form-group">
                  <label for="country" class="form-label label text-secondary"
                    >Country <span class="text-danger">*</span></label
                  >
                  <div class="form-group">
                    <vSelect
                      v-model="country"
                      :options="listCountry"
                      placeholder="Choose Country"
                    ></vSelect>
                  </div>
                  <span class="text-danger">{{ errors.country }}</span>
                </div>
              </div>
              <div class="col-12 mt-5">
                <button
                  class="btn btn-primary fw-semibold text-white py-2 px-3"
                  type="submit"
                >
                  Save
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>
